import { useSyncExternalStore } from 'react';
import { validatePromotionImageFile } from './removeLandingPromoBackground';

const LS_KEY = 'lewouhmart_landing_promo_bg_job_id';
const POLL_MS = 2000;

export type LandingPromoBgJobStatus =
  | 'idle'
  | 'uploading'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export type LandingPromoBgJobState = {
  status: LandingPromoBgJobStatus;
  progress: number;
  stageKey: string;
  /** Relative `/uploads/...` when server job completed */
  serverImageUrl: string | null;
  errorCode: string | null;
  jobId: string | null;
};

export type LandingPromoBgJobMeta = Pick<
  LandingPromoBgJobState,
  'status' | 'serverImageUrl' | 'errorCode' | 'jobId'
>;

const initialState = (): LandingPromoBgJobState => ({
  status: 'idle',
  progress: 0,
  stageKey: '',
  serverImageUrl: null,
  errorCode: null,
  jobId: null,
});

let state: LandingPromoBgJobState = initialState();

let apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
let authorizedFetch: ((url: string, init?: RequestInit) => Promise<Response>) | null = null;

const progressSubscribers = new Set<() => void>();
const metaSubscribers = new Set<() => void>();

let metaSnapshot: LandingPromoBgJobMeta = {
  status: 'idle',
  serverImageUrl: null,
  errorCode: null,
  jobId: null,
};

let pollTimer: ReturnType<typeof setInterval> | null = null;
let activePollJobId: string | null = null;

let progressNotifyRaf = 0;

export function configureLandingPromoImageJobs(
  base: string,
  fetchImpl: (url: string, init?: RequestInit) => Promise<Response>,
) {
  apiBase = base.replace(/\/$/, '');
  authorizedFetch = fetchImpl;
}

function getFetch() {
  return authorizedFetch ?? ((u: string, i?: RequestInit) => fetch(u, i));
}

function syncMetaSnapshot(): boolean {
  const next: LandingPromoBgJobMeta = {
    status: state.status,
    serverImageUrl: state.serverImageUrl,
    errorCode: state.errorCode,
    jobId: state.jobId,
  };
  if (
    metaSnapshot.status === next.status &&
    metaSnapshot.serverImageUrl === next.serverImageUrl &&
    metaSnapshot.errorCode === next.errorCode &&
    metaSnapshot.jobId === next.jobId
  ) {
    return false;
  }
  metaSnapshot = next;
  return true;
}

function notifyProgressSubscribers() {
  progressSubscribers.forEach((l) => l());
}

function notifyMetaSubscribers() {
  metaSubscribers.forEach((l) => l());
}

function cancelProgressNotify() {
  if (progressNotifyRaf) {
    cancelAnimationFrame(progressNotifyRaf);
    progressNotifyRaf = 0;
  }
}

function scheduleProgressNotify() {
  if (progressNotifyRaf) return;
  progressNotifyRaf = requestAnimationFrame(() => {
    progressNotifyRaf = 0;
    notifyProgressSubscribers();
  });
}

function isOnlyProgressUpdate(
  prev: LandingPromoBgJobState,
  next: LandingPromoBgJobState,
): boolean {
  if (prev.status !== next.status) return false;
  if (next.status !== 'queued' && next.status !== 'processing') return false;
  return (
    prev.serverImageUrl === next.serverImageUrl &&
    prev.errorCode === next.errorCode &&
    prev.jobId === next.jobId &&
    (prev.progress !== next.progress || prev.stageKey !== next.stageKey)
  );
}

function setState(next: LandingPromoBgJobState) {
  const prev = state;
  state = next;
  if (isOnlyProgressUpdate(prev, next)) {
    scheduleProgressNotify();
    return;
  }
  cancelProgressNotify();
  const metaChanged = syncMetaSnapshot();
  if (metaChanged) notifyMetaSubscribers();
  notifyProgressSubscribers();
}

function subscribeProgress(cb: () => void) {
  progressSubscribers.add(cb);
  return () => progressSubscribers.delete(cb);
}

function subscribeMeta(cb: () => void) {
  metaSubscribers.add(cb);
  return () => metaSubscribers.delete(cb);
}

function getSnapshot(): LandingPromoBgJobState {
  return state;
}

function getMetaSnapshot(): LandingPromoBgJobMeta {
  return metaSnapshot;
}

export function useLandingPromoBackgroundJob(): LandingPromoBgJobState {
  return useSyncExternalStore(subscribeProgress, getSnapshot, getSnapshot);
}

export function useLandingPromoBackgroundJobMeta(): LandingPromoBgJobMeta {
  return useSyncExternalStore(subscribeMeta, getMetaSnapshot, getMetaSnapshot);
}

export function isLandingPromoBgJobBusy(s: LandingPromoBgJobMeta | LandingPromoBgJobState): boolean {
  return (
    s.status === 'uploading' || s.status === 'queued' || s.status === 'processing'
  );
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  activePollJobId = null;
}

function applyServerJob(payload: {
  status: string;
  progress?: number;
  stageKey?: string;
  url?: string;
  error?: string;
}) {
  const prog = typeof payload.progress === 'number' ? payload.progress : 0;
  const stage = typeof payload.stageKey === 'string' ? payload.stageKey : '';
  const cur = getSnapshot();

  if (payload.status === 'queued') {
    setState({
      ...cur,
      status: 'queued',
      progress: prog,
      stageKey: stage,
    });
    return;
  }
  if (payload.status === 'processing') {
    setState({
      ...cur,
      status: 'processing',
      progress: prog,
      stageKey: stage,
    });
    return;
  }
  if (payload.status === 'completed' && payload.url) {
    stopPolling();
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    setState({
      status: 'completed',
      progress: 100,
      stageKey: '',
      serverImageUrl: payload.url,
      errorCode: null,
      jobId: cur.jobId,
    });
    return;
  }
  if (payload.status === 'failed') {
    stopPolling();
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    const err =
      payload.error === 'job_not_found'
        ? 'job_not_found'
        : (payload.error ?? 'processing_failed');
    setState({
      status: 'failed',
      progress: prog,
      stageKey: stage,
      serverImageUrl: null,
      errorCode: err.slice(0, 200),
      jobId: null,
    });
    return;
  }
  stopPolling();
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
  setState({
    status: 'failed',
    progress: prog,
    stageKey: stage,
    serverImageUrl: null,
    errorCode: (payload.error ?? 'unknown_response').slice(0, 200),
    jobId: null,
  });
}

async function pollOnce(jobId: string) {
  try {
    const res = await getFetch()(
      `${apiBase}/settings/landing-promotion/image-job/${encodeURIComponent(jobId)}`,
    );
    if (!res.ok) {
      applyServerJob({ status: 'failed', error: 'poll_failed' });
      return;
    }
    const data = (await res.json()) as {
      status: string;
      progress?: number;
      stageKey?: string;
      url?: string;
      error?: string;
    };
    applyServerJob(data);
  } catch {
    applyServerJob({ status: 'failed', error: 'network' });
  }
}

function startPolling(jobId: string) {
  if (activePollJobId === jobId && pollTimer) return;
  stopPolling();
  activePollJobId = jobId;
  void pollOnce(jobId);
  pollTimer = setInterval(() => void pollOnce(jobId), POLL_MS);
}

export type StartPromoBgResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'busy' | 'validation' | 'network' | 'server';
      validation?: 'not_image' | 'too_large';
    };

export async function startLandingPromoBackgroundRemoval(
  file: File,
): Promise<StartPromoBgResult> {
  if (isLandingPromoBgJobBusy(getSnapshot())) {
    return { ok: false, reason: 'busy' };
  }
  const v = validatePromotionImageFile(file);
  if (v) {
    return { ok: false, reason: 'validation', validation: v };
  }

  stopPolling();
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }

  setState({
    status: 'uploading',
    progress: 0,
    stageKey: '',
    serverImageUrl: null,
    errorCode: null,
    jobId: null,
  });

  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await getFetch()(
      `${apiBase}/settings/landing-promotion/image/remove-background`,
      { method: 'POST', body: fd },
    );
    if (!res.ok) {
      setState(initialState());
      return { ok: false, reason: 'server' };
    }
    const body = (await res.json()) as { jobId?: string };
    if (!body.jobId) {
      setState(initialState());
      return { ok: false, reason: 'server' };
    }
    try {
      localStorage.setItem(LS_KEY, body.jobId);
    } catch {
      /* ignore */
    }
    setState({
      status: 'queued',
      progress: 0,
      stageKey: '',
      serverImageUrl: null,
      errorCode: null,
      jobId: body.jobId,
    });
    startPolling(body.jobId);
    return { ok: true };
  } catch {
    setState(initialState());
    return { ok: false, reason: 'network' };
  }
}

/** Resume polling after reload or returning to the app (job runs on server). */
export function tryResumeLandingPromoServerJob() {
  if (!authorizedFetch) return;
  let jobId: string | null = null;
  try {
    jobId = localStorage.getItem(LS_KEY);
  } catch {
    return;
  }
  if (!jobId) return;
  if (isLandingPromoBgJobBusy(getSnapshot())) return;

  setState({
    status: 'queued',
    progress: 0,
    stageKey: '',
    serverImageUrl: null,
    errorCode: null,
    jobId,
  });
  startPolling(jobId);
}

export function resetLandingPromoBackgroundJob() {
  stopPolling();
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
  setState(initialState());
}

export function clearLandingPromoJobAfterSave() {
  stopPolling();
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
  setState(initialState());
}

export function dismissLandingPromoBgError() {
  if (getSnapshot().status !== 'failed') return;
  stopPolling();
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
  setState(initialState());
}
